import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { format, differenceInDays, addDays, isBefore, isAfter, isSameDay } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Users, CalendarDays, CreditCard, CheckCircle, ChevronLeft, ArrowRight, Phone } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';
import { getImageUrl } from '../utils/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Booking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preSelectedCabinId = searchParams.get('cabin');
  const { t, language } = useLanguage();
  const { getWhatsAppLink } = useBusinessInfo();
  const dateLocale = language === 'es' ? es : enUS;

  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Booking state - now supports multiple cabins
  const [selectedCabinIds, setSelectedCabinIds] = useState(preSelectedCabinId ? [preSelectedCabinId] : []);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [guestsPerCabin, setGuestsPerCabin] = useState({}); // Map of cabin_id -> guests count
  const [bookedDatesMap, setBookedDatesMap] = useState({}); // Map of cabin_id -> booked dates
  const [pricingDetails, setPricingDetails] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  
  // Guest info
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('transfer');

  // Load cabins
  useEffect(() => {
    const fetchCabins = async () => {
      try {
        const response = await axios.get(`${API}/cabins`);
        setCabins(response.data);
      } catch (error) {
        console.error('Error fetching cabins:', error);
        toast.error(t('booking.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchCabins();
  }, []);

  // Load availability for all cabins when component mounts
  useEffect(() => {
    if (cabins.length === 0) return;
    
    const fetchAllAvailability = async () => {
      const newBookedDatesMap = {};
      for (const cabin of cabins) {
        try {
          const response = await axios.get(`${API}/availability/${cabin.id}`);
          newBookedDatesMap[cabin.id] = response.data.booked_dates.map(d => new Date(d));
        } catch (error) {
          console.error(`Error fetching availability for ${cabin.id}:`, error);
          newBookedDatesMap[cabin.id] = [];
        }
      }
      setBookedDatesMap(newBookedDatesMap);
    };
    fetchAllAvailability();
  }, [cabins]);

  // Calculate dynamic pricing when dates or selected cabins change
  useEffect(() => {
    if (selectedCabinIds.length === 0 || !dateRange.from || !dateRange.to) {
      setPricingDetails(null);
      return;
    }
    
    const fetchPricing = async () => {
      setLoadingPricing(true);
      try {
        const checkIn = format(dateRange.from, 'yyyy-MM-dd');
        const checkOut = format(dateRange.to, 'yyyy-MM-dd');
        
        // Calculate pricing for all selected cabins
        let totalPrice = 0;
        const pricingBreakdown = [];
        
        for (const cabinId of selectedCabinIds) {
          const response = await axios.get(`${API}/pricing/calculate?cabin_id=${cabinId}&check_in=${checkIn}&check_out=${checkOut}`);
          totalPrice += response.data.total_price;
          pricingBreakdown.push({
            cabin_id: cabinId,
            ...response.data
          });
        }
        
        setPricingDetails({
          total_price: totalPrice,
          breakdown: pricingBreakdown,
          total_nights: pricingBreakdown[0]?.total_nights || 0
        });
      } catch (error) {
        console.error('Error fetching pricing:', error);
      } finally {
        setLoadingPricing(false);
      }
    };
    fetchPricing();
  }, [selectedCabinIds, dateRange.from, dateRange.to]);

  // Get selected cabins objects
  const selectedCabins = useMemo(() => 
    cabins.filter(c => selectedCabinIds.includes(c.id)),
    [cabins, selectedCabinIds]
  );

  // Calculate total capacity of selected cabins
  const totalCapacity = useMemo(() => 
    selectedCabins.reduce((sum, cabin) => sum + cabin.capacity, 0),
    [selectedCabins]
  );

  const nights = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return 0;
    return differenceInDays(dateRange.to, dateRange.from);
  }, [dateRange]);

  const totalPrice = useMemo(() => {
    if (pricingDetails) {
      return pricingDetails.total_price;
    }
    if (selectedCabins.length === 0 || nights === 0) return 0;
    return selectedCabins.reduce((sum, cabin) => sum + (nights * cabin.price_per_night), 0);
  }, [selectedCabins, nights, pricingDetails]);

  // Check if a date is disabled for any of the selected cabins
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (isBefore(date, today)) return true;
    
    // A date is disabled if ANY of the selected cabins is booked on that date
    for (const cabinId of selectedCabinIds) {
      const bookedDates = bookedDatesMap[cabinId] || [];
      if (bookedDates.some(bookedDate => isSameDay(date, bookedDate))) {
        return true;
      }
    }
    return false;
  };

  // Toggle cabin selection
  const toggleCabinSelection = (cabinId) => {
    const cabin = cabins.find(c => c.id === cabinId);
    
    setSelectedCabinIds(prev => {
      if (prev.includes(cabinId)) {
        // Remove cabin and its guests count
        setGuestsPerCabin(prevGuests => {
          const newGuests = { ...prevGuests };
          delete newGuests[cabinId];
          return newGuests;
        });
        return prev.filter(id => id !== cabinId);
      } else {
        // Add cabin with default 2 guests (or capacity if less)
        setGuestsPerCabin(prevGuests => ({
          ...prevGuests,
          [cabinId]: Math.min(2, cabin?.capacity || 2)
        }));
        return [...prev, cabinId];
      }
    });
    // Reset dates when cabin selection changes
    setDateRange({ from: undefined, to: undefined });
  };

  // Update guests for a specific cabin
  const updateGuestsForCabin = (cabinId, count) => {
    setGuestsPerCabin(prev => ({
      ...prev,
      [cabinId]: count
    }));
  };

  // Calculate total guests across all cabins
  const totalGuests = useMemo(() => 
    Object.values(guestsPerCabin).reduce((sum, count) => sum + count, 0),
    [guestsPerCabin]
  );

  // Check if a cabin is available for the selected dates
  const isCabinAvailableForDates = (cabinId) => {
    if (!dateRange.from || !dateRange.to) return true;
    const bookedDates = bookedDatesMap[cabinId] || [];
    
    let current = new Date(dateRange.from);
    while (current < dateRange.to) {
      if (bookedDates.some(bookedDate => isSameDay(current, bookedDate))) {
        return false;
      }
      current = addDays(current, 1);
    }
    return true;
  };

  const canProceedStep1 = selectedCabinIds.length > 0 && dateRange.from && dateRange.to && nights > 0 && totalGuests > 0;
  const canProceedStep2 = guestInfo.name && guestInfo.phone && paymentMethod;

  const getCabinDisplayName = (cabin) => {
    if (!cabin) return '';
    if (language === 'es') return cabin.name;
    return cabin.model_type === 'grande' 
      ? `Large Cabin ${cabin.name.slice(-1)}` 
      : `Medium Cabin ${cabin.name.slice(-1)}`;
  };

  const handleSubmit = async () => {
    if (!canProceedStep2) return;

    setSubmitting(true);
    try {
      // Build guests details string for notes
      const guestsDetails = selectedCabins.map(cabin => 
        `${getCabinDisplayName(cabin)}: ${guestsPerCabin[cabin.id] || 0} ${language === 'es' ? 'huéspedes' : 'guests'}`
      ).join(', ');
      
      const notesWithGuests = guestInfo.notes 
        ? `${guestInfo.notes}\n\n${language === 'es' ? 'Distribución de huéspedes' : 'Guest distribution'}: ${guestsDetails}`
        : `${language === 'es' ? 'Distribución de huéspedes' : 'Guest distribution'}: ${guestsDetails}`;

      const response = await axios.post(`${API}/bookings`, {
        cabin_ids: selectedCabinIds,
        guest_name: guestInfo.name,
        guest_phone: guestInfo.phone,
        guest_email: guestInfo.email || null,
        check_in: format(dateRange.from, 'yyyy-MM-dd'),
        check_out: format(dateRange.to, 'yyyy-MM-dd'),
        guests: totalGuests,
        payment_method: paymentMethod,
        notes: notesWithGuests
      });

      setBookingResult(response.data);
      setStep(3);
      toast.success(t('booking.bookingSuccess'));
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || t('booking.bookingError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F2EB]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen bg-[#F5F2EB] pt-24">
      <Toaster position="top-center" richColors />
      
      <div className="container-ribba py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
            {t('booking.subtitle')}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] font-bold mt-3">
            {t('booking.title')}
          </h1>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-4 md:gap-8">
            {[
              { num: 1, label: t('booking.stepSelect'), icon: CalendarDays },
              { num: 2, label: t('booking.stepData'), icon: Users },
              { num: 3, label: t('booking.stepConfirmation'), icon: CheckCircle }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    step >= s.num ? 'bg-[#2F3E30] text-white' : 'bg-white text-[#5A5A5A]'
                  }`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <span className={`mt-2 text-xs font-medium hidden sm:block ${
                    step >= s.num ? 'text-[#2F3E30]' : 'text-[#5A5A5A]'
                  }`}>
                    {s.label}
                  </span>
                </div>
                {idx < 2 && (
                  <div className={`w-12 md:w-24 h-0.5 ${
                    step > s.num ? 'bg-[#2F3E30]' : 'bg-[#E5E0D6]'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Select Cabin & Dates */}
        {step === 1 && (
          <div data-testid="booking-step-1" className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Selection */}
              <div className="bg-white p-6 md:p-8 rounded-sm card-shadow">
                <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">
                  {t('booking.selectCabin')}
                </h2>

                {/* Multi-Cabin Selection */}
                <div className="mb-6">
                  <Label className="text-sm font-medium text-[#5A5A5A] mb-3 block">
                    {language === 'es' ? 'Selecciona las cabañas (1-4)' : 'Select cabins (1-4)'}
                  </Label>
                  <div className="space-y-3">
                    {cabins.map((cabin) => {
                      const isSelected = selectedCabinIds.includes(cabin.id);
                      const isAvailable = isCabinAvailableForDates(cabin.id);
                      const isDisabled = !isAvailable && !isSelected;
                      
                      return (
                        <div 
                          key={cabin.id}
                          className={`p-4 rounded-sm border-2 transition-all ${
                            isSelected 
                              ? 'border-[#2F3E30] bg-[#2F3E30]/5' 
                              : isDisabled
                                ? 'border-gray-200 bg-gray-50 opacity-50'
                                : 'border-[#E5E0D6] hover:border-[#C6A87C]'
                          }`}
                          data-testid={`cabin-option-${cabin.id}`}
                        >
                          <div 
                            className={`flex items-center justify-between ${!isDisabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            onClick={() => !isDisabled && toggleCabinSelection(cabin.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected 
                                  ? 'border-[#2F3E30] bg-[#2F3E30]' 
                                  : 'border-[#E5E0D6]'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-4 h-4 text-white" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-[#1A1A1A]">
                                  {getCabinDisplayName(cabin)}
                                </p>
                                <p className="text-sm text-[#5A5A5A]">
                                  {language === 'es' ? `Hasta ${cabin.capacity} huéspedes` : `Up to ${cabin.capacity} guests`} • {cabin.bedrooms} {language === 'es' ? 'hab.' : 'rooms'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-[#2F3E30]">
                                ${cabin.price_per_night.toLocaleString()}
                              </p>
                              <p className="text-xs text-[#5A5A5A]">
                                /{language === 'es' ? 'noche' : 'night'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Guests selector for selected cabin */}
                          {isSelected && (
                            <div className="mt-3 pt-3 border-t border-[#E5E0D6]" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-between">
                                <Label className="text-sm text-[#5A5A5A]">
                                  {language === 'es' ? 'Huéspedes en esta cabaña:' : 'Guests in this cabin:'}
                                </Label>
                                <Select 
                                  value={(guestsPerCabin[cabin.id] || 1).toString()} 
                                  onValueChange={(value) => updateGuestsForCabin(cabin.id, parseInt(value))}
                                >
                                  <SelectTrigger className="w-24" data-testid={`guests-select-${cabin.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[...Array(cabin.capacity)].map((_, i) => (
                                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                                        {i + 1}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                          
                          {isDisabled && dateRange.from && (
                            <p className="text-xs text-red-500 mt-2">
                              {language === 'es' ? 'No disponible en las fechas seleccionadas' : 'Not available for selected dates'}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedCabinIds.length > 0 && (
                    <div className="mt-3 p-3 bg-[#F5F2EB] rounded-sm">
                      <p className="text-sm text-[#2F3E30] font-medium">
                        {language === 'es' 
                          ? `${selectedCabinIds.length} cabaña(s) • ${totalGuests} huésped(es) en total`
                          : `${selectedCabinIds.length} cabin(s) • ${totalGuests} guest(s) total`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Calendar */}
                <div>
                  <Label className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                    {t('booking.stayDates')}
                  </Label>
                  <div className="border border-[#E5E0D6] rounded-sm p-4">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      disabled={isDateDisabled}
                      numberOfMonths={1}
                      locale={dateLocale}
                      className="mx-auto"
                      data-testid="booking-calendar"
                    />
                  </div>
                  {dateRange.from && dateRange.to && (
                    <p className="mt-3 text-sm text-[#5A5A5A]">
                      {format(dateRange.from, "d MMM", { locale: dateLocale })} - {format(dateRange.to, "d MMM, yyyy", { locale: dateLocale })}
                    </p>
                  )}
                </div>
              </div>

              {/* Right: Summary */}
              <div className="bg-white p-6 md:p-8 rounded-sm card-shadow h-fit sticky top-28">
                <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">
                  {t('booking.summary')}
                </h2>

                {selectedCabins.length > 0 ? (
                  <div className="space-y-4">
                    {/* Show images of selected cabins */}
                    {selectedCabins.length === 1 ? (
                      <div className="aspect-video rounded-sm overflow-hidden mb-4">
                        <img
                          src={selectedCabins[0].images[0]}
                          alt={getCabinDisplayName(selectedCabins[0])}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {selectedCabins.slice(0, 4).map((cabin, idx) => (
                          <div key={cabin.id} className="aspect-video rounded-sm overflow-hidden">
                            <img
                              src={getImageUrl(cabin.images[0])}
                              alt={getCabinDisplayName(cabin)}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Show selected cabins details with guests per cabin */}
                    <div className="space-y-2">
                      {selectedCabins.map(cabin => (
                        <div key={cabin.id} className="flex justify-between items-center p-2 bg-[#F5F2EB] rounded-sm">
                          <div>
                            <p className="font-medium text-[#1A1A1A] text-sm">{getCabinDisplayName(cabin)}</p>
                            <p className="text-xs text-[#5A5A5A]">
                              {guestsPerCabin[cabin.id] || 0} {language === 'es' ? 'huésped(es)' : 'guest(s)'}
                            </p>
                          </div>
                          <p className="font-semibold text-[#2F3E30] text-sm">${cabin.price_per_night.toLocaleString()}/{language === 'es' ? 'noche' : 'night'}</p>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-[#E5E0D6] pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#5A5A5A]">{language === 'es' ? 'Cabañas' : 'Cabins'}</span>
                        <span className="text-[#1A1A1A]">{selectedCabins.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#5A5A5A]">{language === 'es' ? 'Huéspedes totales' : 'Total guests'}</span>
                        <span className="text-[#1A1A1A]">{totalGuests}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#5A5A5A]">{t('booking.nights')}</span>
                        <span className="text-[#1A1A1A]">{nights || '-'}</span>
                      </div>
                    </div>

                    <div className="border-t border-[#E5E0D6] pt-4">
                      <div className="flex justify-between">
                        <span className="font-semibold text-[#1A1A1A]">{t('booking.total')}</span>
                        <div className="text-right">
                          {loadingPricing ? (
                            <div className="w-5 h-5 border-2 border-[#2F3E30] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <span className="font-heading text-2xl font-bold text-[#2F3E30]">
                              ${totalPrice.toLocaleString()} {t('common.mxn')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      data-testid="continue-step-1-btn"
                      onClick={() => setStep(2)}
                      disabled={!canProceedStep1}
                      className="w-full btn-primary mt-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('booking.continue')}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <p className="text-[#5A5A5A] text-center py-8">
                    {t('booking.selectCabinToSee')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Guest Info & Payment */}
        {step === 2 && (
          <div data-testid="booking-step-2" className="max-w-4xl mx-auto">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#2F3E30] mb-6 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>{t('booking.back')}</span>
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Form */}
              <div className="bg-white p-6 md:p-8 rounded-sm card-shadow">
                <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">
                  {t('booking.guestData')}
                </h2>

                <div className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                      {t('booking.fullName')} *
                    </Label>
                    <Input
                      id="name"
                      data-testid="guest-name-input"
                      value={guestInfo.name}
                      onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                      placeholder={t('booking.yourName')}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                      {t('booking.phone')} *
                    </Label>
                    <Input
                      id="phone"
                      data-testid="guest-phone-input"
                      value={guestInfo.phone}
                      onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                      placeholder="+52 33 1234 5678"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                      {t('booking.email')}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="guest-email-input"
                      value={guestInfo.email}
                      onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                      placeholder="tu@email.com"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-sm font-medium text-[#5A5A5A] mb-2 block">
                      {t('booking.notes')}
                    </Label>
                    <Textarea
                      id="notes"
                      data-testid="guest-notes-input"
                      value={guestInfo.notes}
                      onChange={(e) => setGuestInfo({ ...guestInfo, notes: e.target.value })}
                      placeholder={t('booking.notesPlaceholder')}
                      className="w-full"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div className="mt-8">
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-4">
                    {t('booking.paymentMethod')}
                  </h3>
                  <div className="p-4 border border-[#2F3E30] bg-[#2F3E30]/5 rounded-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#2F3E30] rounded-full flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-medium text-[#1A1A1A]">{t('booking.transfer')}</span>
                        <span className="block text-sm text-[#5A5A5A]">
                          {t('booking.transferDesc')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Summary */}
              <div className="bg-white p-6 md:p-8 rounded-sm card-shadow h-fit sticky top-28">
                <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">
                  {t('booking.bookingSummary')}
                </h2>

                <div className="space-y-4">
                  {/* Show images of selected cabins */}
                  {selectedCabins.length === 1 ? (
                    <div className="aspect-video rounded-sm overflow-hidden mb-4">
                      <img
                        src={selectedCabins[0]?.images[0]}
                        alt={getCabinDisplayName(selectedCabins[0])}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {selectedCabins.slice(0, 4).map((cabin, idx) => (
                        <div key={cabin.id} className="aspect-video rounded-sm overflow-hidden">
                          <img
                            src={getImageUrl(cabin.images[0])}
                            alt={getCabinDisplayName(cabin)}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show selected cabins names */}
                  <div className="space-y-2">
                    {selectedCabins.map(cabin => (
                      <div key={cabin.id} className="flex justify-between items-center p-2 bg-[#F5F2EB] rounded-sm">
                        <div>
                          <p className="font-medium text-[#1A1A1A] text-sm">{getCabinDisplayName(cabin)}</p>
                          <p className="text-xs text-[#5A5A5A]">{guestsPerCabin[cabin.id] || 0} {language === 'es' ? 'huésped(es)' : 'guest(s)'}</p>
                        </div>
                        <p className="font-semibold text-[#2F3E30] text-sm">${cabin.price_per_night.toLocaleString()}/{language === 'es' ? 'noche' : 'night'}</p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-[#E5E0D6] pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5A5A5A]">{language === 'es' ? 'Cabañas seleccionadas' : 'Selected cabins'}</span>
                      <span className="text-[#1A1A1A]">{selectedCabins.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5A5A5A]">{language === 'es' ? 'Huéspedes totales' : 'Total guests'}</span>
                      <span className="text-[#1A1A1A]">{totalGuests}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5A5A5A]">{t('booking.checkIn')}</span>
                      <span className="text-[#1A1A1A]">
                        {dateRange.from && format(dateRange.from, "d MMM yyyy", { locale: dateLocale })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5A5A5A]">{t('booking.checkOut')}</span>
                      <span className="text-[#1A1A1A]">
                        {dateRange.to && format(dateRange.to, "d MMM yyyy", { locale: dateLocale })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#5A5A5A]">{t('booking.nights')}</span>
                      <span className="text-[#1A1A1A]">{nights}</span>
                    </div>
                  </div>

                  <div className="border-t border-[#E5E0D6] pt-4">
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#1A1A1A]">{t('booking.total')}</span>
                      <span className="font-heading text-2xl font-bold text-[#2F3E30]">
                        ${totalPrice.toLocaleString()} {t('common.mxn')}
                      </span>
                    </div>
                  </div>

                  <button
                    data-testid="confirm-booking-btn"
                    onClick={handleSubmit}
                    disabled={!canProceedStep2 || submitting}
                    className="w-full btn-primary mt-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('booking.processing')}
                      </>
                    ) : (
                      <>
                        {t('booking.confirmBooking')}
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && bookingResult && (
          <div data-testid="booking-step-3" className="max-w-2xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-sm card-shadow text-center">
              <div className="w-20 h-20 bg-[#3D6F48]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#3D6F48]" />
              </div>

              <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mb-3">
                {t('booking.confirmed')}
              </h2>
              <p className="text-[#5A5A5A] mb-8">
                {t('booking.confirmedDesc')}
              </p>

              <div className="bg-[#F5F2EB] p-6 rounded-sm text-left mb-8">
                <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4">
                  {t('booking.bookingDetails')}
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5A5A5A]">{t('booking.confirmationNumber')}</span>
                    <span className="font-mono text-[#2F3E30] font-medium">{bookingResult.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A5A]">{t('booking.cabin')}</span>
                    <span className="text-[#1A1A1A]">{bookingResult.cabin_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A5A]">{t('booking.checkIn')}</span>
                    <span className="text-[#1A1A1A]">{format(new Date(bookingResult.check_in), "d MMM, yyyy", { locale: dateLocale })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A5A]">{t('booking.checkOut')}</span>
                    <span className="text-[#1A1A1A]">{format(new Date(bookingResult.check_out), "d MMM, yyyy", { locale: dateLocale })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A5A]">{t('booking.nights')}</span>
                    <span className="text-[#1A1A1A]">{bookingResult.total_nights}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A5A]">{t('booking.paymentMethod')}</span>
                    <span className="text-[#1A1A1A]">{t('booking.transfer')}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-[#E5E0D6]">
                    <span className="font-semibold text-[#1A1A1A]">{t('booking.totalToPay')}</span>
                    <span className="font-heading text-xl font-bold text-[#2F3E30]">
                      ${bookingResult.total_price.toLocaleString()} {t('common.mxn')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-[#C6A87C]/10 p-6 rounded-sm text-left mb-4">
                <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-2">
                  {t('booking.nextStep')}
                </h3>
                <p className="text-[#5A5A5A] text-sm">
                  {t('booking.nextStepDesc')}
                </p>
              </div>

              {/* WhatsApp confirmation notice */}
              <div className="bg-[#25D366]/10 border border-[#25D366]/30 p-4 rounded-sm text-left mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-[#1A1A1A] text-sm">
                    <strong>{language === 'es' ? '📱 Confirmación por WhatsApp' : '📱 WhatsApp Confirmation'}</strong><br/>
                    {t('booking.whatsappConfirmation')}
                  </p>
                </div>
              </div>

              {/* Cancellation policy notice */}
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm text-left mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="text-sm">
                    <strong className="text-amber-800">
                      {language === 'es' ? '📋 Política de Cancelación' : '📋 Cancellation Policy'}
                    </strong>
                    <p className="text-amber-700 mt-1">
                      {language === 'es' 
                        ? 'Para cancelar tu reservación, contáctanos por WhatsApp. El porcentaje de reembolso dependerá de la anticipación con la que realices la cancelación.'
                        : 'To cancel your reservation, contact us via WhatsApp. The refund percentage will depend on how far in advance you make the cancellation.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={getWhatsAppLink(language === 'es' 
                    ? `Hola, acabo de hacer una reservación (${bookingResult.id.slice(0, 8).toUpperCase()}) para ${bookingResult.cabin_name}. ¿Me pueden enviar los datos para el pago?`
                    : `Hello, I just made a reservation (${bookingResult.id.slice(0, 8).toUpperCase()}) for ${bookingResult.cabin_name}. Can you send me the payment details?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-confirmation-btn"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  {t('booking.contactWhatsApp')}
                </a>
                <Link
                  to="/"
                  className="btn-secondary inline-flex items-center justify-center"
                >
                  {t('booking.backToHome')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;
